import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import API_CONFIG from '../../config/api';
import PropostaPublicaEditalDialog from './PropostaPublicaEditalDialog';

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const statusChip = (status) => {
  const s = String(status || 'ABERTO').toUpperCase();
  if (s === 'ENCERRADO') return { label: 'Encerrado', color: 'default' };
  if (s === 'AGENDADO') return { label: 'Agendado', color: 'info' };
  return { label: 'Aberto', color: 'success' };
};

/**
 * Lista pública de editais — Baixar TR + Enviar proposta (sem login).
 */
export default function EditaisPublicosList({ compact = false }) {
  const [editais, setEditais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [editalProposta, setEditalProposta] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await fetch(`${API_CONFIG.BACKEND_URL}/api/editais/abertos`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Não foi possível carregar os editais.');
      }
      const data = await res.json();
      setEditais(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setErro(e.message || 'Erro ao carregar editais.');
      setEditais([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const baixarTr = (edital) => {
    window.open(
      `${API_CONFIG.BACKEND_URL}/api/public/editais/${encodeURIComponent(edital.codigo)}/tr`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (erro) {
    return (
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        {erro}
      </Alert>
    );
  }

  if (!editais.length) {
    return (
      <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
        Nenhum edital público aberto no momento.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        {editais.map((edital) => {
          const chip = statusChip(edital.status);
          const encerrado = String(edital.status || '').toUpperCase() === 'ENCERRADO';
          return (
            <Grid item xs={12} sm={compact ? 12 : 6} md={compact ? 12 : 4} key={edital.id || edital.codigo}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid rgba(21,101,192,0.12)',
                  background:
                    'linear-gradient(160deg, rgba(255,255,255,0.98) 0%, rgba(227,242,253,0.55) 100%)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={800}
                    color="primary.dark"
                    sx={{ lineHeight: 1.3 }}
                  >
                    {edital.objeto || edital.titulo || edital.codigo}
                  </Typography>
                  <Chip size="small" label={chip.label} color={chip.color} sx={{ fontWeight: 700 }} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Código: <strong>{edital.codigo}</strong>
                  {edital.unidade ? ` · Unidade: ${edital.unidade}` : ''}
                </Typography>
                {(edital.dataAbertura || edital.dataEncerramento) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {edital.dataAbertura ? `Abertura: ${formatDate(edital.dataAbertura)}` : ''}
                    {edital.dataAbertura && edital.dataEncerramento ? ' · ' : ''}
                    {edital.dataEncerramento
                      ? `Encerramento: ${formatDate(edital.dataEncerramento)}`
                      : ''}
                  </Typography>
                )}
                {edital.descricao && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {edital.descricao}
                  </Typography>
                )}
                <Box flexGrow={1} />
                <Stack spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => baixarTr(edital)}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 700 }}
                  >
                    Baixar TR (PDF)
                  </Button>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SendIcon />}
                    disabled={encerrado}
                    onClick={() => setEditalProposta(edital)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 2,
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                    }}
                  >
                    Enviar proposta (sem login)
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <PropostaPublicaEditalDialog
        open={Boolean(editalProposta)}
        edital={editalProposta}
        onClose={() => setEditalProposta(null)}
        onEnviado={() => {
          /* mantém dialog aberto com sucesso; usuário fecha */
        }}
      />
    </>
  );
}
